/* eslint-disable max-lines-per-function, max-lines, complexity */
import * as React from 'react'
import {
  BadgeCheck,
  ChevronDown,
  ChevronUp,
  GitMerge,
  GraduationCap,
  Link2,
  LogIn,
  Mail,
  MoreHorizontal,
  Pencil,
  Receipt,
  Ticket,
} from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EmptyState } from '@/components/ui/empty-state'
import { Textarea } from '@/components/ui/textarea'
import { ApiError, deleteJSON, getJSON, patchJSON, postJSON } from '@/lib/api'
import { TimelineFilterBar, TimelineFilteredEmpty } from './TimelineFilterBar'
import { timelineParams, useTimelineFilters, type TimelineFacets } from './timelineFilters'
import { useTimelineFeed } from './useTimelineFeed'

type NoteDetail = {
  body: string
  author_name: string
  author_id: number
  created_at: string
  edited_at: string | null
}
type TimelineEntry = {
  id: number
  kind: string
  family: string
  occurred_at: string
  copy: string
  link?: { id: number; type: string } | null
  owner?: string | null
  detail?: Record<string, unknown> | null
}
export type TimelineResponse = TimelineFacets & {
  entries: TimelineEntry[]
  next_cursor: string | null
  total_count: number
  families: string[]
  pinned_note?: Note | null
  viewer?: { seat_id: number | null; can_write: boolean; is_admin: boolean }
}
type Note = {
  id: number
  body: string | null
  author_name: string
  author_id: number
  created_at: string
  edited_at: string | null
  pinned: boolean
}
type Version = { body: string; editor_name: string; edited_at: string }

const MAX_BODY = 5000
const WARN_AT = 4500
const icons: Record<string, React.ComponentType<{ className?: string }>> = {
  events: Ticket,
  learning: GraduationCap,
  money: Receipt,
  email: Mail,
  portal: LogIn,
  membership: BadgeCheck,
  relationships: Link2,
  profile_changes: Pencil,
}

function dayLabel(value: string, associationDate: string) {
  const date = new Date(value)
  const today = new Date(associationDate)
  const days = Math.round(
    (new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime() -
      new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()) /
      86_400_000,
  )
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)
}
function timeLabel(value: string) {
  return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(
    new Date(value),
  )
}
function initials(name: string) {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}
function noteFromEntry(entry: TimelineEntry, pinnedId?: number): Note | null {
  if (entry.family !== 'notes' || !entry.detail) return null
  const detail = entry.detail as unknown as NoteDetail
  return {
    id: entry.link?.id ?? entry.id,
    body: detail.body,
    author_name: detail.author_name,
    author_id: detail.author_id,
    created_at: detail.created_at,
    edited_at: detail.edited_at,
    pinned: pinnedId === (entry.link?.id ?? entry.id),
  }
}

function SystemRow({ entry }: { entry: TimelineEntry }) {
  const [expanded, setExpanded] = React.useState(false)
  const Icon = icons[entry.family] ?? Pencil
  return (
    <div className="min-h-9 border-b border-border py-2">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-primary/10 text-primary">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1 text-body-sm text-foreground">
          {entry.link?.type === 'contact' ? (
            <a className="underline-offset-2 hover:underline" href={`/contacts/${entry.link.id}`}>
              {entry.copy}
            </a>
          ) : (
            entry.copy
          )}
          {entry.owner && (
            <span className="ml-1.5 rounded-sm bg-muted px-1.5 py-0.5 text-caption text-muted-foreground">
              {entry.owner}
            </span>
          )}
        </div>
        <span className="shrink-0 text-caption tabular-nums text-muted-foreground">
          {timeLabel(entry.occurred_at)}
        </span>
        {entry.detail ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            aria-label={expanded ? 'Hide detail' : 'Show detail'}
            onClick={() => setExpanded((open) => !open)}
          >
            {expanded ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </Button>
        ) : (
          <span className="w-6 shrink-0" />
        )}
      </div>
      {expanded && entry.detail && (
        <pre className="whitespace-pre-wrap pl-9 pt-1 text-caption text-muted-foreground">
          {JSON.stringify(entry.detail)}
        </pre>
      )}
    </div>
  )
}

function ErasedNoteRow({ entry }: { entry: TimelineEntry }) {
  return (
    <div className="flex gap-3 border-b border-border py-3">
      <Avatar className="h-6 w-6">
        <AvatarFallback>{initials(entry.owner ?? 'Note')}</AvatarFallback>
      </Avatar>
      <p className="text-caption text-muted-foreground">{entry.copy}</p>
    </div>
  )
}

function NoteBlock({
  contactId,
  note,
  currentUserId,
  canWrite,
  isAdmin,
  onChanged,
}: {
  contactId: number
  note: Note
  currentUserId?: number
  canWrite: boolean
  isAdmin: boolean
  onChanged: () => void
}) {
  const [editing, setEditing] = React.useState(false)
  const [value, setValue] = React.useState(note.body ?? '')
  const [versions, setVersions] = React.useState<Version[] | null>(null)
  const [confirmDelete, setConfirmDelete] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const author = currentUserId === undefined || currentUserId === note.author_id
  const erased = note.body === null
  const loadVersions = async () => {
    setVersions(
      (
        await getJSON<{ versions: Version[] }>(
          `/api/v1/contacts/${contactId}/notes/${note.id}/versions`,
        )
      ).versions,
    )
  }
  const saveEdit = async () => {
    try {
      await patchJSON(`/api/v1/contacts/${contactId}/notes/${note.id}`, { note: { body: value } })
      setEditing(false)
      onChanged()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Unable to save note')
    }
  }
  const deleteNote = async () => {
    await deleteJSON(`/api/v1/contacts/${contactId}/notes/${note.id}`)
    setConfirmDelete(false)
    onChanged()
  }
  const togglePin = async () => {
    await patchJSON(
      `/api/v1/contacts/${contactId}/notes/${note.id}/${note.pinned ? 'unpin' : 'pin'}`,
      {},
    )
    onChanged()
  }
  if (erased)
    return (
      <div className="flex gap-3 py-3">
        <Avatar className="h-6 w-6">
          <AvatarFallback>{initials(note.author_name)}</AvatarFallback>
        </Avatar>
        <p className="text-caption text-muted-foreground">
          Note by {note.author_name}, {timeLabel(note.created_at)}
        </p>
      </div>
    )
  const items = canWrite
    ? [
        ...(author ? [{ label: 'Edit', action: () => setEditing(true) }] : []),
        ...(note.pinned
          ? [{ label: 'Unpin', action: () => void togglePin() }]
          : [{ label: 'Pin', action: () => void togglePin() }]),
        ...(note.body && (author || isAdmin)
          ? [{ label: 'Delete', action: () => setConfirmDelete(true) }]
          : []),
      ]
    : []
  return (
    <>
      <div className="flex gap-3 border-b border-border py-3">
        <Avatar className="h-6 w-6">
          <AvatarFallback>{initials(note.author_name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          {editing ? (
            <NoteEditor
              value={value}
              onChange={setValue}
              onCancel={() => setEditing(false)}
              onSave={saveEdit}
              saveLabel="Save"
              error={error}
            />
          ) : (
            <>
              <div className="flex items-baseline gap-1.5 text-body-sm">
                <span className="font-medium text-foreground">{note.author_name}</span>
                <span className="text-caption text-muted-foreground">
                  · {timeLabel(note.created_at)}
                </span>
                {note.edited_at && (
                  <span className="text-caption text-muted-foreground">
                    · edited {timeLabel(note.edited_at)}
                  </span>
                )}
              </div>
              <p className="whitespace-pre-wrap pt-0.5 text-body-sm text-foreground">{note.body}</p>
              {versions && (
                <ul className="space-y-0.5 pt-1 text-caption text-muted-foreground">
                  {versions.map((version, index) => (
                    <li key={index}>
                      {version.editor_name} · {timeLabel(version.edited_at)}: {version.body}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
        {items.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                aria-label="Note actions"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {items.map((item) => (
                <DropdownMenuItem
                  key={item.label}
                  destructive={item.label === 'Delete'}
                  onSelect={item.action}
                >
                  {item.label}
                </DropdownMenuItem>
              ))}
              {note.body && note.edited_at && (
                <DropdownMenuItem onSelect={() => void loadVersions()}>
                  View versions
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete note?</DialogTitle>
            <DialogDescription>This note will be removed from the Timeline.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void deleteNote()}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function NoteEditor({
  value,
  onChange,
  onCancel,
  onSave,
  saveLabel,
  error,
}: {
  value: string
  onChange: (value: string) => void
  onCancel: () => void
  onSave: () => void
  saveLabel: string
  error: string | null
}) {
  const overBy = value.length - MAX_BODY
  const disabled = value.trim().length === 0 || overBy > 0
  return (
    <div>
      <Textarea
        rows={4}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="resize-none"
        aria-label="Note body"
      />
      {error && <p className="pt-1 text-caption text-destructive">{error}</p>}
      <div className="flex min-h-9 items-center justify-between gap-2 pt-2">
        {value.length > WARN_AT ? (
          <span
            className={
              overBy > 0 ? 'text-caption text-destructive' : 'text-caption text-muted-foreground'
            }
          >
            {overBy > 0
              ? `Over the ${MAX_BODY.toLocaleString()} character limit by ${overBy}`
              : `${value.length.toLocaleString()} / ${MAX_BODY.toLocaleString()}`}
          </span>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" disabled={disabled} onClick={onSave}>
            {saveLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

function PinnedNoteBlock({
  contactId,
  note,
  canWrite,
  onChanged,
}: {
  contactId: number
  note: Note
  canWrite: boolean
  onChanged: () => void
}) {
  const [error, setError] = React.useState<string | null>(null)
  const unpin = async () => {
    try {
      await patchJSON(`/api/v1/contacts/${contactId}/notes/${note.id}/unpin`, {})
      onChanged()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Unable to unpin note')
    }
  }
  return (
    <div className="flex items-center gap-3 rounded-md border border-primary/20 bg-primary/5 px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="text-caption font-medium uppercase tracking-wide text-primary">Pinned</p>
        <p className="whitespace-pre-wrap break-words text-body-sm text-foreground">{note.body}</p>
        <p className="text-caption text-muted-foreground">
          — {note.author_name} ·{' '}
          {new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(
            new Date(note.created_at),
          )}
        </p>
      </div>
      {canWrite && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="icon" aria-label="Pinned note actions">
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => void unpin()}>Unpin</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {error && <span className="text-caption text-destructive">{error}</span>}
    </div>
  )
}

function Composer({ contactId, onSaved }: { contactId: number; onSaved: () => void }) {
  const [open, setOpen] = React.useState(false)
  const [value, setValue] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const save = async () => {
    try {
      await postJSON(`/api/v1/contacts/${contactId}/notes`, { note: { body: value } })
      setValue('')
      setOpen(false)
      onSaved()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Unable to save note')
    }
  }
  return (
    <div className="flex gap-3">
      <Avatar className="h-6 w-6 shrink-0">
        <AvatarFallback>ME</AvatarFallback>
      </Avatar>
      <div
        className="min-w-0 flex-1"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node) && !value.trim()) {
            setOpen(false)
          }
        }}
      >
        {open ? (
          <NoteEditor
            value={value}
            onChange={(nextValue) => {
              setValue(nextValue)
              setError(null)
            }}
            onCancel={() => {
              setValue('')
              setOpen(false)
            }}
            onSave={() => void save()}
            saveLabel="Add note"
            error={error}
          />
        ) : (
          <Textarea
            placeholder="Add a note…"
            rows={1}
            value={value}
            onFocus={() => setOpen(true)}
            onChange={(event) => setValue(event.target.value)}
            className="h-9 min-h-9 resize-none py-2"
            aria-label="Note composer"
          />
        )}
      </div>
    </div>
  )
}

export function ContactTimelineTab({
  contactId,
  associationDate,
  tombstone,
  canWrite = true,
  isAdmin = false,
  onContactChanged,
}: {
  contactId: number
  associationDate?: string
  tombstone?: { id: number; name: string }
  canWrite?: boolean
  isAdmin?: boolean
  onContactChanged?: () => void
}) {
  const { filters, change, clear } = useTimelineFilters()
  const { pages, facets, loading, error, failed, load } = useTimelineFeed(
    contactId,
    timelineParams(filters).toString(),
  )
  const viewer = facets?.viewer
  const cursor = pages.at(-1)?.next_cursor
  const entries = pages.flatMap((page) => page.entries)
  const pinnedNote = facets?.pinned_note
  const pinnedId = pinnedNote?.id
  const effectiveCanWrite = canWrite && (viewer?.can_write ?? true)
  const effectiveIsAdmin = viewer?.is_admin ?? isAdmin
  const grouped = entries.reduce<Record<string, TimelineEntry[]>>((groups, entry) => {
    const label = dayLabel(entry.occurred_at, associationDate ?? new Date().toISOString())
    groups[label] ??= []
    groups[label].push(entry)
    return groups
  }, {})
  if (!facets && !failed && !error) return <EmptyState title="Loading timeline" />
  const refresh = () => {
    void load()
    onContactChanged?.()
  }
  return (
    <div className="max-w-3xl space-y-4">
      {tombstone ? (
        <div className="flex h-9 items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 text-body-sm">
          <GitMerge className="size-4 text-primary" />
          <span>Merged into</span>
          <a
            className="font-medium underline-offset-2 hover:underline"
            href={`/contacts/${tombstone.id}`}
          >
            {tombstone.name}
          </a>
        </div>
      ) : (
        effectiveCanWrite && <Composer contactId={contactId} onSaved={refresh} />
      )}
      {!tombstone && pinnedNote && (
        <PinnedNoteBlock
          contactId={contactId}
          note={pinnedNote}
          canWrite={effectiveCanWrite}
          onChanged={refresh}
        />
      )}
      <TimelineFilterBar
        filters={filters}
        facets={facets ?? { families: [] }}
        onChange={change}
        onClear={clear}
        error={error}
      />
      {loading && (
        <p role="status" className="text-caption text-muted-foreground">
          Loading timeline
        </p>
      )}
      {failed && (
        <EmptyState
          title="Timeline unavailable"
          action={
            <Button variant="outline" onClick={() => void load()}>
              Try again
            </Button>
          }
        />
      )}
      {Object.entries(grouped).map(([label, group]) => (
        <section key={label}>
          <h3 className="pb-1 pt-2 text-caption font-medium text-muted-foreground">{label}</h3>
          {group.map((entry) => {
            const note = noteFromEntry(entry, pinnedId)
            return note ? (
              <NoteBlock
                key={entry.id}
                contactId={contactId}
                note={note}
                currentUserId={viewer?.seat_id ?? undefined}
                canWrite={effectiveCanWrite}
                isAdmin={effectiveIsAdmin}
                onChanged={refresh}
              />
            ) : entry.family === 'notes' ? (
              <ErasedNoteRow key={entry.id} entry={entry} />
            ) : (
              <SystemRow key={entry.id} entry={entry} />
            )
          })}
        </section>
      ))}
      {!loading && !failed && !error && entries.length === 0 && (
        <TimelineFilteredEmpty
          filters={filters}
          facets={facets ?? { families: [] }}
          onClear={clear}
        />
      )}
      {cursor && (
        <div className="flex items-center justify-center gap-3 py-2">
          <Button
            type="button"
            variant="outline"
            disabled={loading}
            onClick={() => void load(cursor)}
          >
            Show 50 more · {entries.length} of {pages.at(-1)?.total_count ?? entries.length} shown
          </Button>
        </div>
      )}
    </div>
  )
}
